// Shared Mass-Balance COMPUTE engine — pure, React-free, runnable in the browser AND in a Node Lambda.
// This is the SINGLE source of truth for the balance math so the UI and Cal never diverge (Phase 4).
// Parsing lives in massBalanceOas.ts / massBalanceRefinery.ts (also pure); this module takes parsed
// rows and produces the balance, yield/slate, KPIs and the analytics snapshot.
import * as XLSX from 'xlsx';
import { ProductMapping, SUBCATS, CATEGORIES, catLabel, heuristicSubCat } from './massBalanceMapping';
import { YieldData, YieldRow, InvRow } from './massBalanceOas';
import { FuelLossReport } from './massBalanceFuelLoss';

const norm = (s: any) => String(s ?? '').trim().toLowerCase();
const numAt = (row: any[], i: number): number => {
  const v = row?.[i];
  const n = typeof v === 'number' ? v : parseFloat(String(v).replace(/,/g, ''));
  return isNaN(n) ? 0 : n;
};

// ---------- stream / balance types ----------
// `key` is a stable per-grade code (the OAS SAP number) used ONLY to join the volume and mass
// balances. `product` is a DESCRIPTION and the two reports do not always spell it the same way
// (2025-09: "JOCA (Johan Castberg)" in m3 vs "Johan Castberg" in MT, same SAP 210010251).
export interface Stream { key?: string; product: string; opening: number; closing: number; ship: number; rec: number; recl: number; prod: number; }
export type Category = 'crude' | 'product';
export interface BalanceRow {
  key?: string;
  product: string;
  opening: number; receipts: number; production: number; shipments: number; reclass: number;
  theoretical: number; closing: number; gainLoss: number;
  category: Category; active: boolean;
}

export function computeBalance(streams: Stream[]): BalanceRow[] {
  return streams.map((s) => {
    // Production is stored as a magnitude; its sign (crude=consumed → subtract,
    // product=made → add) is whichever makes the balance close. Auto-detect per row.
    const base = s.opening + s.rec - s.ship - s.recl;
    const addExpr = base + s.prod;   // production ADDED (a made product)
    const subExpr = base - s.prod;   // production SUBTRACTED (crude charged)
    const useAdd = Math.abs(addExpr - s.closing) <= Math.abs(subExpr - s.closing);
    const theoretical = useAdd ? addExpr : subExpr;
    const productionSigned = useAdd ? s.prod : -s.prod;
    let category: Category;
    const nameU = s.product.toUpperCase();
    if (/(CRUDE|DILBIT|BITUMEN)/.test(nameU)) category = 'crude';
    else if (productionSigned < -1) category = 'crude';
    else if (productionSigned > 1) category = 'product';
    else category = s.rec > s.ship ? 'crude' : 'product';
    const active = [s.opening, s.closing, s.rec, s.ship, s.recl, s.prod].some((v) => Math.abs(v) > 0.5);
    return {
      key: s.key,
      product: s.product,
      opening: s.opening, receipts: s.rec, production: productionSigned,
      shipments: s.ship, reclass: s.recl,
      theoretical, closing: s.closing, gainLoss: theoretical - s.closing,
      category, active,
    };
  });
}

// OAS→Refinery adapter. The OAS "Daily Yields" is a per-grade production balance; map each YieldRow
// onto the Refinery Stream model. VERIFIED: the ledger closes on
//   closing = opening + receipts − deliveries + intakeProd   (residual 0 for every grade).
// ADJUSTMENTS is the gross→net production reconciliation (NET = INTAKE&PROD + ADJUSTMENTS), NOT a stock
// reclass — so reclass=0 here (feeding it in manufactured a phantom loss). Own-consumption is already
// encoded in intakeProd for crude, so it must NOT be subtracted again.
export function yieldDataToStreams(yd: YieldData): Stream[] {
  return yd.rows.map((r) => ({
    key: r.sap, product: r.desc || r.sap,
    opening: r.opening, closing: r.closing, rec: r.receipts, ship: r.deliveries,
    recl: 0, prod: Math.abs(r.intakeProd),
  }));
}

// Derived mass (MT) for a volume-only OAS dataset: scale each grade's m³ by its density (t/m³ == kg/L).
export function yieldDataToMassStreams(yd: YieldData, densityOf: (row: YieldRow) => number | null): { streams: Stream[]; coverageVol: number } {
  let volTotal = 0; let volCovered = 0;
  const streams = yd.rows.map((r) => {
    const rho = densityOf(r) || 0;
    const throughput = Math.abs(r.receipts) + Math.abs(r.deliveries) + Math.abs(r.intakeProd);
    volTotal += throughput; if (rho > 0) volCovered += throughput;
    return {
      key: r.sap, product: r.desc || r.sap,
      opening: r.opening * rho, closing: r.closing * rho, rec: r.receipts * rho,
      ship: r.deliveries * rho, recl: 0, prod: Math.abs(r.intakeProd) * rho,
    };
  });
  return { streams, coverageVol: volTotal ? volCovered / volTotal : 0 };
}

// Map a Daily-Yields GROUP (the OAS dataset's own classification) to a Refinery sub-category.
export function groupSubCat(group: string): string {
  const g = (group || '').toUpperCase();
  if (/(CRUDE|CONDENSATE|\bBLEND\b)/.test(g)) return 'feedstock-crude';
  if (/(NAT GAS|NATURAL GAS)/.test(g)) return 'feedstock-natgas';
  if (/(\bH2\b|HYDROGEN)/.test(g)) return 'feedstock-h2';
  if (/(ADDITIVE|IMPORTS?|\bGTL\b|\bFAME\b|ETHANOL|ALCOHOL)/.test(g)) return 'feedstock-other';
  if (/(PROPANE|BUTANE|\bLPG\b|PROPYLENE)/.test(g)) return 'products-lpg';
  if (/(BENZENE)/.test(g)) return 'products-benzene';
  if (/(TOLUENE)/.test(g)) return 'products-toluene';
  if (/(SULPHUR|SULFUR)/.test(g)) return 'products-sulphur';
  if (/(BITUMEN|ASPHALT)/.test(g)) return 'products-asphalt';
  if (/(JET|KERO|AVTUR|DIESEL|\bMGO\b|GAS ?OIL|\bLGO\b|\bHCO\b|\bLCO\b|CYCLE|DISTILLATE|CUTTER|\bDPK\b|\bULSD\b|SOUR (DIESEL|KERO))/.test(g)) return 'products-distillate';
  if (/(GASOLINE|NAPHTHA|MOGAS|PLATFORMATE|REFORMATE|ALKYLATE|\bTOPS\b|RAFFINATE|HCCG|LCCG|FRCCG|PU10|SU10|V POWER GAS|ISO ?HEXANE|DUAL|MISC MOGAS|E ?MIX|HEART CUT|\bBFS\b)/.test(g)) return 'products-gasoline';
  if (/(SLOP)/.test(g)) return 'intermediates-slop';
  if (/(\bVGO\b|WAX|\bHVI\b|LUBE|\bLMO\b|\bSPO\b|\bDAO\b|MVIP|BASE ?OIL|PLATFORMATE|EB CONCENTRATE|MISC)/.test(g)) return 'intermediates-other';
  if (/(FUEL OIL|RESIDUE|LONG RES|\bFO\b|M100|\bTPO\b|SLURRY|DECANT|COKE|OWN USE|FLARE|REFINERY GAS|FUEL GAS)/.test(g)) return 'byproducts-other';
  return '';
}

// Curated density (t/m³) by Daily-Yields GROUP name, for minor groups with no measured density.
export function groupDensity(group: string): number | null {
  const g = (group || '').toUpperCase();
  if (/(REFINERY GAS|FUEL GAS|OWN USE GAS|\bGAS\b(?!\s*OIL))/.test(g)) return null;
  if (/(PROPYLENE)/.test(g)) return 0.52;
  if (/(PROPANE)/.test(g)) return 0.51; if (/(BUTANE)/.test(g)) return 0.58; if (/(LPG)/.test(g)) return 0.55;
  if (/(SULPHUR|SULFUR)/.test(g)) return 1.80; if (/(BITUMEN|ASPHALT)/.test(g)) return 1.02;
  if (/(COKE)/.test(g)) return 0.90; if (/(SLURRY|DECANT)/.test(g)) return 1.02;
  if (/(WAX|SLACK ?WAX)/.test(g)) return 0.80;
  if (/(HVI|LUBE|\bLMO\b|\bSPO\b|\bDAO\b|MVIP|BASE ?OIL)/.test(g)) return 0.87;
  if (/(FUEL OIL|RESIDUE|LONG RES|\bFO\b|M100|TPO|BFS)/.test(g)) return 0.96;
  if (/(NAPHTHA|\bTOPS\b|ISO ?HEXANE|MOGAS|GASOLINE)/.test(g)) return 0.72;
  if (/(BENZENE)/.test(g)) return 0.88; if (/(TOLUENE)/.test(g)) return 0.87;
  if (/(JET|KERO|AVTUR)/.test(g)) return 0.80;
  if (/(DIESEL|\bMGO\b|GAS ?OIL|\bLGO\b|\bHCO\b|\bLCO\b|CYCLE)/.test(g)) return 0.85;
  if (/(ETHANOL|ALCOHOL|FAME|GTL)/.test(g)) return 0.79;
  if (/(CRUDE|CONDENSATE|\bBLEND\b)/.test(g)) return 0.84;
  return null;
}

// Yield & own-use + product slate + KPIs + In/Out balance — the analytics the user sees. Pure: the
// per-dataset mapping + OAS Group-column assignments are passed in (no React state).
export function computeYield(rowsArg: BalanceRow[], mapping: ProductMapping | null, oasAssignments?: Record<string, string>) {
  const subs = mapping?.subCategories || SUBCATS;
  const cats = mapping?.categories || CATEGORIES;
  const sid = (r: BalanceRow) => mapping?.assignments[r.product] || oasAssignments?.[r.product] || heuristicSubCat(r.product) || '';
  const subDef = (id: string) => subs.find((s) => s.id === id);
  let totalRec = 0, totalShip = 0, madeTotal = 0, ownUse = 0, feedReceived = 0, dist = 0, gaso = 0;
  let gainLossTotal = 0, reclassTotal = 0, sumOpen = 0, sumProd = 0, sumClose = 0;
  let crudeProc = 0, otherFeedProc = 0, netProducts = 0, otherNetProducts = 0, flaring = 0;
  let hasCrude = false, hasOtherFeed = false, hasProducts = false, hasOtherProducts = false, hasFlaring = false;
  const bySub = new Map<string, any>();
  const lossRows: BalanceRow[] = [];
  rowsArg.forEach((r) => {
    totalRec += r.receipts; totalShip += r.shipments; gainLossTotal += r.gainLoss;
    sumOpen += r.opening; sumProd += r.production; sumClose += r.closing;
    reclassTotal += Math.max(0, r.reclass);
    const id = sid(r); const sd = subDef(id); const cat = sd?.category || '';
    const consumed = Math.max(0, -r.production);
    const isFlare = /\bflar/i.test(r.product);
    if (cat === 'feedstock') {
      feedReceived += r.receipts;
      if (id === 'feedstock-crude') { hasCrude = true; crudeProc += consumed; }
      else { hasOtherFeed = true; otherFeedProc += consumed; }
    }
    if (isFlare) { hasFlaring = true; flaring += Math.abs(r.production); }
    else if (cat === 'products') { hasProducts = true; netProducts += r.production; }
    else if (cat === 'intermediates' || cat === 'byproducts') { hasOtherProducts = true; otherNetProducts += r.production; }
    if (r.production > 0) madeTotal += r.production;
    if (cat === 'byproducts') { ownUse += r.production; if (Math.abs(r.production) > 0.5) lossRows.push(r); }
    if (cat !== 'feedstock') { if (sd?.label === 'Gasoline') gaso += r.production; else if (sd?.label === 'Distillate' || sd?.label === 'Gas Oil') dist += r.production; }
    const gr = bySub.get(id) || { id, label: sd ? `${catLabel(sd.category)} · ${sd.label}` : (id ? `? · ${id}` : 'Unassigned'), cat, section: cat === 'feedstock' ? 'input' : 'output', prodPos: 0, net: 0 };
    if (r.production > 0) gr.prodPos += r.production;
    gr.net += r.production;
    bySub.set(id, gr);
  });
  const slate: any[] = [];
  cats.forEach((c) => subs.filter((s) => s.category === c.id).forEach((s) => { const g = bySub.get(s.id); if (g && Math.abs(g.net) > 0.5) slate.push(g); }));
  const knownSub = new Set(subs.map((s) => s.id));
  Array.from(bySub.entries()).filter(([id, g]) => !knownSub.has(id) && Math.abs(g.net) > 0.5).forEach(([, g]) => slate.push(g));
  // Base the section shares on the SUM OF MAGNITUDES so the per-category % add up to 100% even when some
  // output categories are net-negative (intermediate components consumed into blends, or reclassified
  // grades) — using the signed sum made the magnitudes total >100%.
  const outTotal = slate.filter((g) => g.section === 'output').reduce((a, g) => a + Math.abs(g.net), 0);
  const inTotal = slate.filter((g) => g.section === 'input').reduce((a, g) => a + Math.max(0, -g.net), 0);
  lossRows.sort((a, b) => b.production - a.production);
  const kpi = { crudeProc, otherFeedProc, netProducts, otherNetProducts, flaring, reclass: reclassTotal, hasCrude, hasOtherFeed, hasProducts, hasOtherProducts, hasFlaring, hasReclass: Math.abs(reclassTotal) > 0.5 };
  return { totalRec, totalShip, madeTotal, outTotal, inTotal, ownUse, feedReceived, volGain: totalShip - totalRec, slate, dist, gaso, lossRows, bySub, kpi, gainLossTotal,
    balIn: sumOpen + totalRec + sumProd, balOut: totalShip + sumClose };
}

// Assemble the persisted analytics snapshot from the two-basis balances. Used by the compute Lambda
// (and available to the frontend). `oasAdjustment*` are the OAS gross→net production totals (magnitude).
export function buildRefinerySnapshot(args: {
  balV: BalanceRow[]; balM: BalanceRow[] | null;
  mapping: ProductMapping | null; oasAssignments?: Record<string, string>;
  dataset: string; period: string; meta?: Record<string, any>;
  oasAdjustment?: number | null; oasAdjustmentMass?: number | null;
  fuelLoss?: FuelLossReport | null;
  /** Per-stream split of Own Fuel, where the source publishes it (condensed MT export only). */
  fuelComponents?: { sap: string; name: string; mass: number }[];
}) {
  const { balV, balM, mapping, oasAssignments } = args;
  const yV = computeYield(balV, mapping, oasAssignments);
  const yM = balM && balM.length ? computeYield(balM, mapping, oasAssignments) : null;
  const subs = mapping?.subCategories || SUBCATS;
  const sidOf = (p: string) => mapping?.assignments[p] || oasAssignments?.[p] || heuristicSubCat(p) || '';
  const labelOf = (id: string) => { const s = subs.find((x) => x.id === id); return s ? `${catLabel(s.category)} · ${s.label}` : (id ? `? · ${id}` : 'Unassigned'); };
  // ---- per-grade rows: the UNION of the two bases, not the volume balance with mass bolted on ----
  // EET publishes the same balance twice and the two reports do NOT cover the same grade set: the MT
  // report carried 470,565 MT of throughput in 2026-06 (171,596 MT of it crude) on grades absent from
  // the m3 report, and 75,345 MT in 2026-08. Keying off balV alone dropped all of it, so mass totals
  // derived from `grades` ran up to 20.6% under `kpi.mass` -- which is computed from balM directly and
  // was right all along. Join on the SAP code, not the description (see Stream.key).
  const joinKey = (r: BalanceRow) => r.key || r.product;
  const massByKey = new Map<string, BalanceRow>();
  for (const r of (balM || [])) if (!massByKey.has(joinKey(r))) massByKey.set(joinKey(r), r);
  const massLeg = (m: BalanceRow) => ({ opening: m.opening, receipts: m.receipts, production: m.production,
    shipments: m.shipments, reclass: m.reclass, closing: m.closing, gainLoss: m.gainLoss });
  const gradeRow = (id: string, product: string, basis: 'both' | 'volume' | 'mass',
                    v: BalanceRow | null, m: BalanceRow | null) => ({
    product, category: id, subcat: labelOf(id), basis,
    opening: v ? v.opening : null, receipts: v ? v.receipts : null, production: v ? v.production : null,
    shipments: v ? v.shipments : null, reclass: v ? v.reclass : null, closing: v ? v.closing : null,
    gainLoss: v ? v.gainLoss : null,
    mass: m ? massLeg(m) : null,
  });
  // A grade may appear twice in one sheet; the mass leg must be claimed once or it is counted twice.
  const claimed = new Set<string>();
  const grades = balV.map((r) => {
    const k = joinKey(r); const m = claimed.has(k) ? undefined : massByKey.get(k);
    if (m) claimed.add(k);
    return gradeRow(sidOf(r.product), r.product, m ? 'both' : 'volume', r, m || null);
  });
  // Grades reported in tonnes but not in m3. Volume legs are null rather than 0 so a volume-basis
  // consumer SKIPS them (every reader already guards on null) instead of reading an absent
  // measurement as a real zero -- which would manufacture phantom stock breaks.
  for (const r of (balM || [])) {
    const k = joinKey(r);
    if (claimed.has(k)) continue;
    claimed.add(k);
    grades.push(gradeRow(sidOf(r.product), r.product, 'mass', null, r));
  }
  const slate = yV.slate.map((g: any) => {
    const base = g.section === 'input' ? yV.inTotal : yV.outTotal;
    return { id: g.id, label: g.label, category: g.cat, section: g.section, net: g.net, pct: base ? (Math.abs(g.net) / base) * 100 : 0 };
  });
  const kpiOf = (Y: any) => (Y ? { crudeProc: Y.kpi.crudeProc, otherFeedProc: Y.kpi.otherFeedProc, netProducts: Y.kpi.netProducts, otherNetProducts: Y.kpi.otherNetProducts, flaring: Y.kpi.flaring, reclass: Y.kpi.reclass } : null);
  return {
    meta: { ...(args.meta || {}), dataset: args.dataset, period: args.period },
    kpi: { volume: kpiOf(yV), mass: kpiOf(yM) },
    balance: {
      inputs: { volume: yV.balIn, mass: yM?.balIn ?? null },
      outputs: { volume: yV.balOut, mass: yM?.balOut ?? null },
      loss: { volume: yV.gainLossTotal, mass: yM?.gainLossTotal ?? null },
      adjustment: { volume: args.oasAdjustment ?? yV.kpi.reclass, mass: args.oasAdjustmentMass ?? yM?.kpi?.reclass ?? null },
    },
    slate, grades,
    // The refinery's own Fuel & Loss accounting (Own/Total Fuel, Unaccounted Loss, Flare, BS&W, yields,
    // monthly loss trend for OAS; the Loss/Gain line for AORA) — read from the customer workbook, since the
    // stock reconciliation above closes to ~0 and does NOT represent physical loss. null if not available.
    fuelLoss: args.fuelLoss
      ? { ...args.fuelLoss, fuelComponents: args.fuelComponents?.length ? args.fuelComponents : undefined }
      : null,
  };
}

// ---------- AORA production tab parsing (Toledo/refinery-native) ----------
// Volume/Mass sheet columns (0-indexed): 0 product | 5 opening | 6 closing | 10 shipments |
// 11 receipts | 12 reclass | 15 production. Data starts after the "Products" marker row.
export function parseProductionTab(ws: XLSX.WorkSheet): Stream[] {
  const rows = XLSX.utils.sheet_to_json<any[]>(ws, { header: 1, blankrows: false });
  const out: Stream[] = [];
  let started = false;
  for (const row of rows) {
    const name = row?.[0];
    if (!started) { if (norm(name) === 'products') started = true; continue; }
    if (!name || typeof name !== 'string') continue;
    const hasNum = [5, 6, 10, 11, 12, 15].some((i) => typeof row[i] === 'number');
    if (!hasNum) continue;
    out.push({
      product: String(name).trim(),
      opening: numAt(row, 5), closing: numAt(row, 6),
      ship: numAt(row, 10), rec: numAt(row, 11), recl: numAt(row, 12), prod: numAt(row, 15),
    });
  }
  return out;
}

// ---------- orchestrators: parsed files → balances (shared by UI loadOasMonth/onProd AND the Lambda) ----------
// OAS dataset: Daily-Yields balance + three-tier density-derived mass + Group-column categorization +
// the gross ADJUSTMENTS totals. `materialRows` come from the OAS Material Download (for measured density).
// `yd` is the VOLUME (m3) per-grade balance. `ydMass`, when the site also published the MT variant of the
// same balance ("DAILY OAS MB"), is used AS-IS for the mass balance -- measured tonnes, no density
// estimate. Without it, mass is derived from measured/average densities and flagged as an estimate.
export function buildOasBalances(yd: YieldData, materialRows: InvRow[], ydMass: YieldData | null = null) {
  const densBySap = new Map<string, number[]>();
  for (const r of materialRows) {
    if (r.density != null && r.sap && r.density > 0.3 && r.density < 1.6) {
      const a = densBySap.get(r.sap) || []; a.push(r.density); densBySap.set(r.sap, a);
    }
  }
  const balV = computeBalance(yieldDataToStreams(yd));
  const avg = (arr: number[]) => arr.reduce((a, b) => a + b, 0) / arr.length;
  const sapDens = new Map<string, number>(); densBySap.forEach((arr, k) => sapDens.set(k, avg(arr)));
  const grpAcc = new Map<string, number[]>();
  yd.rows.forEach((r) => { const d = sapDens.get(r.sap); if (d) { const a = grpAcc.get(r.group) || []; a.push(d); grpAcc.set(r.group, a); } });
  const grpDens = new Map<string, number>(); grpAcc.forEach((arr, k) => grpDens.set(k, avg(arr)));
  const densityOfRow = (r: YieldRow): number | null => sapDens.get(r.sap) ?? grpDens.get(r.group) ?? groupDensity(r.group);
  let balM: BalanceRow[] | null;
  let coverageVol: number;
  let oasAdjustmentMass: number | null;
  const massMeasured = !!(ydMass && ydMass.rows.length);
  // The MT and m3 reports do NOT agree on grade descriptions -- September 2026 matches on only 29 of
  // 83 ("SaharanBlend Bejaia" vs "Saharan Blend", "WTI Midland" vs "WTI Midlands", "Jet A-1" vs
  // "JET A1") -- and the condensed MT layout carries no Group column at all. SAP is the stable key and
  // matched 83/83, so adopt the m3 report's description and group wherever the SAP matches. Everything
  // downstream keys off the description -- the product mapping, category assignment, the cross-basis
  // join -- so without this the measured mass would land under 54 unrecognised product names.
  const volBySap = new Map((yd.rows || []).map((r) => [String(r.sap || '').trim(), r]));
  const massRows = (ydMass?.rows || []).map((r) => {
    const v = volBySap.get(String(r.sap || '').trim());
    return v ? { ...r, desc: v.desc || r.desc, group: r.group || v.group } : r;
  });
  // What the refinery BURNED, split by stream -- the 'Fuel' column of the condensed MT export
  // (Refinery Fuel Gas / Coke / Fuel Oil, SAPs 4500000xx). Only that layout carries it: the older
  // "Movements" sheet puts CRUDE CHARGE in its OWN CONSUMPTION column (WTI Midland 280,358 etc.),
  // which is a completely different quantity, so reading it as fuel would be badly wrong. Gated on
  // the condensed sheet name for exactly that reason.
  const condensedMass = /^(MT|M3)$/i.test(String(ydMass?.sheet || ''));
  const fuelComponents = condensedMass
    ? massRows.filter((r) => Math.abs(r.ownCons) > 0.5)
        .map((r) => ({ sap: r.sap, name: r.desc || r.sap, mass: r.ownCons }))
        .sort((a, b) => b.mass - a.mass)
    : [];
  if (massMeasured) {
    balM = computeBalance(yieldDataToStreams({ ...ydMass!, rows: massRows }));
    coverageVol = 1;
    oasAdjustmentMass = massRows.reduce((a, r) => a + Math.abs(r.adjustments || 0), 0);
  } else {
    const derived = yieldDataToMassStreams(yd, densityOfRow);
    coverageVol = derived.coverageVol;
    balM = coverageVol > 0.5 ? computeBalance(derived.streams) : null;
    oasAdjustmentMass = balM ? yd.rows.reduce((a, r) => a + Math.abs((r.adjustments || 0) * (densityOfRow(r) || 0)), 0) : null;
  }
  const oasAssignments: Record<string, string> = {};
  [...yd.rows, ...massRows].forEach((r) => { const p = r.desc || r.sap; const sc = groupSubCat(r.group); if (p && sc) oasAssignments[p] = sc; });
  const oasAdjustment = yd.rows.reduce((a, r) => a + Math.abs(r.adjustments || 0), 0);
  return { balV, balM, coverageVol, oasAssignments, oasAdjustment, oasAdjustmentMass, massMeasured, fuelComponents };
}

// Split a month's yields workbooks by basis, newest first within each basis. A month may carry the m3
// report, the MT report, or both; callers use the volume one as primary and the MT one for measured mass.
export function pickYieldsByBasis(cands: { yd: YieldData; uploadedAt?: string }[]): { vol: YieldData | null; mass: YieldData | null } {
  const newest = (b: 'volume' | 'mass') => {
    const list = cands.filter((c) => c.yd.basis === b && c.yd.rows.length)
      .sort((a, b2) => String(b2.uploadedAt || '').localeCompare(String(a.uploadedAt || '')));
    // From September 2026 EET also sends a CONDENSED m3 export (single sheet "M3"), which is the same
    // balance but carries NO Group column, no Adjustments and no MTD fuel & loss block. Newest-wins
    // alone let it displace the full "Daily Yields" workbook, which left 30 of 88 grades with no
    // category at all and crude processed reading 649,836 instead of 999,410 m3. Prefer a candidate
    // that actually carries groups; fall back to newest within the same tier.
    const rich = list.find((c) => c.yd.rows.some((r) => String(r.group || '').trim()));
    return (rich || list[0])?.yd || null;
  };
  return { vol: newest('volume'), mass: newest('mass') };
}

// AORA dataset: the twin Volume/Mass production sheets → volume & mass balances.
export function buildAoraBalances(prodWb: XLSX.WorkBook): { balV: BalanceRow[]; balM: BalanceRow[] | null } {
  const hasV = prodWb.SheetNames.includes('Volume');
  const hasM = prodWb.SheetNames.includes('Mass');
  const balV = hasV ? computeBalance(parseProductionTab(prodWb.Sheets['Volume'])) : [];
  const balM = hasM ? computeBalance(parseProductionTab(prodWb.Sheets['Mass'])) : null;
  return { balV, balM };
}

// Read an xlsx byte array into a workbook (shared so the Node Lambda doesn't import `xlsx` directly —
// esbuild then resolves the single xlsx from web-app/node_modules for both the app and the Lambda).
export function readWorkbook(bytes: Uint8Array): XLSX.WorkBook {
  return XLSX.read(bytes, { type: 'array', cellDates: true });
}
